import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setUser } from 'store/slices/user-slice';
import { getDomainAndSubdomain } from 'utils/domain';

export const useApp = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, isLoading, getAccessTokenSilently, getIdTokenClaims, loginWithRedirect } = useAuth0();

  useEffect(() => {
    if (isLoading) return;

    const fetchIdToken = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        const idToken = await getIdTokenClaims();

        const roles = idToken?.['https://duck.energy/roles']?.map((role: string) => role.toLowerCase()) ?? [];

        if (roles.includes('admin')) {
          // A full admin URL (e.g. the deployed admin Static Web App) takes precedence over
          // the subdomain-derived admin.<sub>.<domain> host, which needs DNS that may not exist
          // (e.g. in the demo environment).
          const adminUrl = process.env.REACT_APP_ADMIN_URL;
          if (adminUrl) {
            window.location.replace(adminUrl);
            return;
          }

          const { subdomain } = getDomainAndSubdomain();
          const env = process.env.REACT_APP_ENV;
          const adminAreaBaseDomain = process.env.REACT_APP_ADMIN_AREA_URL;

          const redirectURL =
            env === 'production'
              ? `admin.${subdomain}.${adminAreaBaseDomain}`
              : `test.admin.${subdomain}.${adminAreaBaseDomain}`;

          window.location.replace(`https://${redirectURL}`);
          return;
        }

        if (accessToken && idToken) {
          dispatch(setUser({ accessToken, idToken }));
        }
      } catch (error: any) {
        if (error.error === 'login_required') {
          console.warn('Auth0 session not found — redirecting to login');
          await loginWithRedirect();
        } else {
          console.error('Error during Auth0 token fetch:', error);
        }
      }
    };

    if (!isAuthenticated) return;
    fetchIdToken();
  }, [isLoading, isAuthenticated, dispatch, getAccessTokenSilently, getIdTokenClaims, loginWithRedirect]);

  return { isAuthenticated };
};
